import React, { useRef, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { it } from "date-fns/locale";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { fmtDateIT } from "../../lib/dateFormat";

/* Campi data/ora SEMPRE in formato italiano (gg/mm/aaaa, 24h), qualunque sia
   la lingua del browser o del sistema operativo: i campi nativi del browser
   (<input type="date">, <input type="time">) ereditano il locale dell'OS e
   su macchine non italiane mostrano MM/DD/YYYY e AM/PM. Qui il formato lo
   decide il prodotto. Il valore verso il backend resta ISO (YYYY-MM-DD). */

const toISO = (d) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "";
const fromISO = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`) : undefined);

export const DateInputIT = ({ id, value, onChange, min, max, disabled, invalid, className = "", testId, placeholder = "gg/mm/aaaa" }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          data-testid={testId}
          aria-invalid={invalid || undefined}
          className={`wl-input flex items-center justify-between gap-2 text-left font-normal ${invalid ? "is-error" : ""} ${className}`}
        >
          <span className={value ? "" : "text-text-muted"}>{fmtDateIT(value) || placeholder}</span>
          <CalendarIcon size={14} className="text-text-muted shrink-0" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          locale={it}
          weekStartsOn={1}
          selected={fromISO(value)}
          defaultMonth={fromISO(value)}
          disabled={(d) => Boolean((min && toISO(d) < String(min).slice(0, 10)) || (max && toISO(d) > String(max).slice(0, 10)))}
          onSelect={(d) => { onChange(toISO(d)); setOpen(false); }}
        />
      </PopoverContent>
    </Popover>
  );
};

const normalizza = (t) => {
  const m = String(t).match(/^(\d{1,2}):(\d{1,2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return `${String(h).padStart(2, "0")}:${String(mi).padStart(2, "0")}`;
};

export const TimeInputIT = ({ id, value, onChange, disabled, invalid, className = "", testId, placeholder = "HH:MM" }) => {
  const [draft, setDraft] = useState(value || "");
  const emesso = useRef(value || "");
  if ((value || "") !== emesso.current) {
    emesso.current = value || "";
    if (draft !== emesso.current) setDraft(emesso.current);
  }
  const emetti = (v) => { emesso.current = v; onChange(v); };
  const digita = (raw) => {
    let t = raw.replace(/[^\d:]/g, "").slice(0, 5);
    if (/^\d{3,4}$/.test(t)) t = `${t.slice(0, 2)}:${t.slice(2)}`;
    setDraft(t);
    emetti(/^([01]\d|2[0-3]):([0-5]\d)$/.test(t) ? t : "");
  };
  const blur = () => {
    const n = normalizza(draft);
    if (n) { setDraft(n); emetti(n); }
  };
  return (
    <input
      id={id}
      type="text"
      inputMode="numeric"
      value={draft}
      placeholder={placeholder}
      maxLength={5}
      onChange={(e) => digita(e.target.value)}
      onBlur={blur}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      className={`wl-input ${invalid ? "is-error" : ""} ${className}`}
      data-testid={testId}
    />
  );
};
